// FTR F19 — embedded excel viewer (lazy-loaded)
// follows ftr-ledger pattern: viewer has its own X button (showClose), no wrapping modal header
// inner component applies highlights via hooks (must be inside EnvProvider)

import {useState, useEffect} from 'react'
import type {HighlightTarget} from './types'
import {S} from './styles'

// lazy-loaded module type
type ViewerMod = Awaited<typeof import('@ui/mod/excel/viewer')>

export function ExcelViewerEmbed({buffer, filename, sheetIndex, highlight, onClose}: {
	buffer: ArrayBuffer
	filename: string
	sheetIndex?: number
	highlight?: HighlightTarget
	onClose?: () => void
}) {
	const [loaded, setLoaded] = useState<{mod: ViewerMod; env: ReturnType<ViewerMod['workbook_env_make']>} | null>(null)

	useEffect(() => {
		;(async () => {
			try {
				const mod = await import('@ui/mod/excel/viewer')
				const env = mod.workbook_env_make(
					{src: buffer, filename},
					sheetIndex != null ? {activeSheet: sheetIndex} : undefined,
				)
				setLoaded({mod, env})
			} catch (e) {
				console.error('Failed to load excel viewer', e)
			}
		})()
	}, [buffer, filename, sheetIndex])

	if (!loaded) return <div style={{...S.center, padding: 40}}><div style={S.spinner} /></div>

	const {mod, env} = loaded

	return (
		<mod.Workbook.EnvProvider env={env}>
			<ViewerWithHighlights mod={mod} highlight={highlight} onClose={onClose} />
		</mod.Workbook.EnvProvider>
	)
}

// --

/** inner component: resolves highlight targets to CellRange[] using hooks (inside EnvProvider) */
function ViewerWithHighlights({mod, highlight, onClose}: {
	mod: ViewerMod
	highlight?: HighlightTarget
	onClose?: () => void
}) {
	const [, setHighlights] = mod.useHighlights()
	const [, setActiveSheet] = mod.useActiveSheet()
	const data = mod.useWorkbook()

	useEffect(() => {
		if (!highlight) {
			setHighlights([])
			return
		}

		// resolve sheet index by name (fuzzy)
		let sheetIndex = 0
		if (highlight.sheet) {
			const target = highlight.sheet.toLowerCase().trim()
			let idx = data.sheets.findIndex(s => s.name.toLowerCase().trim() === target)
			if (idx < 0) idx = data.sheets.findIndex(s =>
				s.name.toLowerCase().includes(target) || target.includes(s.name.toLowerCase()),
			)
			if (idx >= 0) sheetIndex = idx
		}
		setActiveSheet(sheetIndex)

		// resolve rows to highlight
		const sheet = data.sheets[sheetIndex]
		if (!sheet) {
			setHighlights([])
			return
		}

		// direct row indices (0-based data rows, ie. from _row_index)
		if (highlight.rows && highlight.rows.length > 0) {
			setHighlights(highlight.rows.map(row => ({sheet: sheetIndex, startRow: row, endRow: row})))
			return
		}

		// column+value match: find column index from title row, then matching data rows
		if (highlight.column && highlight.value) {
			const titleRow = sheet.rows[0]
			if (!titleRow) { setHighlights([]); return }

			// find column index by key (rows is ViewerCell[][])
			const colIdx = titleRow.findIndex(c => {
				const v = c.value != null ? String(c.value).trim() : ''
				return v === highlight.column
			})

			if (colIdx < 0) { setHighlights([]); return }

			// find matching data rows
			const ranges: import('@ui/mod/excel/viewer').CellRange[] = []
			for (let r = 1; r < sheet.rows.length; r++) {
				const cell = sheet.rows[r]?.[colIdx]
				const v = cell?.value != null ? String(cell.value).trim() : ''
				if (v === highlight.value) {
					ranges.push({sheet: sheetIndex, startRow: r, endRow: r, startCol: colIdx, endCol: colIdx})
				}
			}
			setHighlights(ranges)
			return
		}

		setHighlights([])
	}, [highlight, data.sheets, setHighlights, setActiveSheet])

	return <mod.Workbook.WorkbookViewer height="100%" showClose={!!onClose} onClose={onClose} />
}
